import React, { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { 
  X,
  Send,
  Upload,
  AlertCircle
} from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { useLanguage } from "../contexts/LanguageContext";
import { publicAPI } from "../api/publicAPI";
import { 
  validateName, 
  validatePhoneNumber, 
  validateMessage, 
  validateEmail,
  getCountryCodes 
} from "../utils/formValidation";

const ContactModal = ({ isOpen, onClose, title }) => {
  const { toast } = useToast();
  const { t, currentLanguage } = useLanguage();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    countryCode: "",
    phone: "",
    message: "",
    file: null
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const countryCodes = getCountryCodes();

  if (!isOpen) return null;

  const validateField = (name, value) => {
    let validation;
    
    switch(name) {
      case 'name':
        validation = validateName(value);
        break;
      case 'email':
        validation = validateEmail(value);
        break;
      case 'phone':
        validation = validatePhoneNumber(value, (formData.countryCode || '').split('__')[0]);
        break;
      case 'message':
        validation = validateMessage(value);
        break;
      default:
        return { isValid: true };
    }
    
    return validation;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData({
      ...formData,
      [name]: value
    });

    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    if (!value) return;
    
    const validation = validateField(name, value);
    if (!validation.isValid) {
      setErrors({
        ...errors,
        [name]: validation.error
      });
    }
  };

  const handleCountryCodeChange = (code) => {
    setFormData({
      ...formData,
      countryCode: code
    });
    if (formData.phone) {
      const validation = validatePhoneNumber(formData.phone, (code || '').split('__')[0]);
      if (!validation.isValid) {
        setErrors({
          ...errors,
          phone: validation.error
        });
      } else {
        setErrors({
          ...errors,
          phone: null
        });
      }
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.size <= 6 * 1024 * 1024) {
      setFormData({ ...formData, file });
      setErrors({ ...errors, file: null });
    } else {
      toast({
        title: t('error'),
        description: currentLanguage === "RU" 
          ? "Файл слишком большой. Максимальный размер 6 МБ." 
          : "File too large. Maximum size is 6 MB.",
        variant: "destructive"
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const newErrors = {};
    const nameValidation = validateName(formData.name);
    const emailValidation = validateEmail(formData.email);
    const phoneValidation = validatePhoneNumber(formData.phone, (formData.countryCode || '').split('__')[0]);
    const messageValidation = validateMessage(formData.message);
    
    if (!nameValidation.isValid) newErrors.name = nameValidation.error;
    if (!emailValidation.isValid) newErrors.email = emailValidation.error;
    if (!phoneValidation.isValid) newErrors.phone = phoneValidation.error;
    if (!messageValidation.isValid) newErrors.message = messageValidation.error;
    
    if (!formData.file) {
      newErrors.file = currentLanguage === "RU" 
        ? "Необходимо прикрепить файл"
        : "File attachment is required";
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast({
        title: currentLanguage === "RU" ? "Ошибка валидации" : "Validation Error",
        description: currentLanguage === "RU" 
          ? "Пожалуйста, исправьте ошибки в форме"
          : "Please fix the errors in the form",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData = new FormData();
      submitData.append('name', nameValidation.cleaned);
      submitData.append('email', formData.email);
      submitData.append('phone', phoneValidation.formatted);
      submitData.append('message', messageValidation.cleaned);
      submitData.append('language', currentLanguage);
      submitData.append('file', formData.file);

      await publicAPI.submitInquiry(submitData);

      toast({
        title: currentLanguage === "RU" ? "Заявка отправлена!" : "Request sent!",
        description: currentLanguage === "RU" 
          ? "Мы свяжемся с вами в ближайшее время для консультации."
          : "We will contact you soon for consultation.",
        duration: 5000
      });

      setFormData({
        name: "",
        email: "",
        countryCode: "",
        phone: "",
        message: "",
        file: null
      });
      setErrors({});
      onClose();

    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast({
        title: currentLanguage === "RU" ? "Ошибка" : "Error",
        description: currentLanguage === "RU"
          ? "Не удалось отправить заявку. Пожалуйста, попробуйте позже."
          : "Failed to submit request. Please try again later.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition z-10"
          aria-label="Закрыть"
        >
          <X className="h-5 w-5 text-gray-500" />
        </button>

        {/* Header */}
        <div className="p-6 pb-4 border-b">
          <h2 className="text-xl font-bold text-gray-900 pr-8">
            {title || (currentLanguage === "RU" ? "Оставить заявку" : "Submit Request")}
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            {currentLanguage === "RU" 
              ? "Заполните форму и мы свяжемся с вами" 
              : "Fill out the form and we will contact you"}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Name */}
          <div className="space-y-1">
            <Label htmlFor="modal-name" className="text-sm">{t('yourName')} *</Label>
            <Input
              id="modal-name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              onBlur={handleBlur}
              required
              className={`${errors.name ? 'border-red-500' : ''}`}
              placeholder={currentLanguage === "RU" ? "Иван Иванов" : "John Smith"}
            />
            {errors.name && (
              <div className="flex items-center space-x-1 text-red-600 text-xs">
                <AlertCircle className="h-3 w-3" />
                <span>{errors.name}</span>
              </div>
            )}
          </div>

          {/* Email */}
          <div className="space-y-1">
            <Label htmlFor="modal-email" className="text-sm">{t('email')} *</Label>
            <Input
              id="modal-email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              onBlur={handleBlur}
              required
              className={`${errors.email ? 'border-red-500' : ''}`}
              placeholder="example@mail.com"
            />
            {errors.email && (
              <div className="flex items-center space-x-1 text-red-600 text-xs">
                <AlertCircle className="h-3 w-3" />
                <span>{errors.email}</span>
              </div>
            )}
          </div>

          {/* Phone */}
          <div className="space-y-1">
            <Label htmlFor="modal-phone" className="text-sm">{t('phone')} *</Label>
            <div className="flex space-x-2">
              <Select value={formData.countryCode} onValueChange={handleCountryCodeChange}>
                <SelectTrigger className="w-28">
                  <SelectValue placeholder={currentLanguage === "RU" ? "Регион" : "Region"} />
                </SelectTrigger>
                <SelectContent>
                  {countryCodes.map((country) => (
                    <SelectItem key={`${country.code}-${country.country}`} value={`${country.code}__${country.country}`}>
                      {country.code} {country.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                id="modal-phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                onBlur={handleBlur}
                required
                className={`flex-1 ${errors.phone ? 'border-red-500' : ''}`}
                placeholder={(formData.countryCode || '').split('__')[0] === "+7" ? "9001234567" : "1234567890"}
              />
            </div>
            {errors.phone && (
              <div className="flex items-center space-x-1 text-red-600 text-xs">
                <AlertCircle className="h-3 w-3" />
                <span>{errors.phone}</span>
              </div>
            )}
          </div>

          {/* Message */}
          <div className="space-y-1">
            <Label htmlFor="modal-message" className="text-sm">{t('message')} *</Label>
            <Textarea
              id="modal-message"
              name="message"
              rows={3}
              value={formData.message}
              onChange={handleInputChange}
              onBlur={handleBlur}
              required
              className={`${errors.message ? 'border-red-500' : ''}`}
              placeholder={currentLanguage === "RU" 
                ? "Опишите ваш запрос..." 
                : "Describe your request..."}
            />
            {errors.message && (
              <div className="flex items-center space-x-1 text-red-600 text-xs">
                <AlertCircle className="h-3 w-3" />
                <span>{errors.message}</span>
              </div>
            )}
          </div>

          {/* File Upload */}
          <div className="space-y-1">
            <Label htmlFor="modal-file" className="text-sm">{t('sendFile')} <span className="text-red-500">*</span></Label>
            <div className={`border-2 border-dashed rounded-lg p-4 text-center hover:border-orange-400 transition ${
              errors.file ? 'border-red-300 bg-red-50' : 'border-gray-300'
            }`}>
              <Upload className="mx-auto h-8 w-8 text-gray-400" />
              <label htmlFor="modal-file" className="cursor-pointer">
                <span className="mt-2 block text-sm font-medium text-gray-900">
                  {t('clickToUpload')}
                </span>
                <span className="block text-xs text-gray-500">
                  {t('maxSize')}
                </span>
              </label>
              <input
                id="modal-file"
                name="file"
                type="file"
                className="sr-only"
                onChange={handleFileChange}
                accept=".doc,.docx,.pdf,.txt,.xlsx"
                required
              />
              {formData.file && (
                <p className="mt-2 text-sm text-orange-600 font-medium">
                  {formData.file.name}
                </p>
              )}
              {errors.file && (
                <p className="mt-1 text-xs text-red-600">{errors.file}</p>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            size="lg"
            disabled={isSubmitting}
            className="w-full bg-orange-500 hover:bg-orange-600"
          >
            {isSubmitting ? (
              "Отправляем..."
            ) : (
              <>
                {t('sendRequest')}
                <Send className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>

          <p className="text-xs text-gray-500 text-center">
            {t('agreeToProcessing')}
          </p>
        </form>
      </div>
    </div>
  );
};

export default ContactModal;
